import { Icon } from '../components/Icon.tsx';
import { useAuthStore } from '../store/auth.ts';
import { dv } from '../i18n/dv.ts';
import { displayName } from '../lib/utils.ts';
import { hasZone } from '../features/zone/access.ts';
import { TelegramCard } from '../features/zone/TelegramCard.tsx';

const OFFICE_PROFILE_URL = 'https://office.wapplify.app/profile';

export function Profile(): JSX.Element {
  const user = useAuthStore((s) => s.user);

  if (!user) {
    return (
      <div className="page">
        <p role="status" aria-live="polite" className="status-label">{dv.common.loading}</p>
      </div>
    );
  }

  const shownName = displayName(user);
  const initials = (shownName || user.email).slice(0, 2).toUpperCase();

  return (
    <div className="page max-w-xl space-y-8">
      {/* Identity header */}
      <header className="flex items-center gap-4">
        <div className="size-14 shrink-0 rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))] flex items-center justify-center font-serif text-xl">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="status-label">{dv.auth.profile}</p>
          <h1 className="font-serif text-[2rem] leading-none mt-1 text-ink truncate tracking-tight">
            {shownName}
          </h1>
          <p className="mt-1 mono-meta text-muted-foreground truncate" dir="ltr">{user.email}</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <span className="pill pill-neutral">{user.designationEn ?? (user.role ? dv.roles[user.role] : '')}</span>
            {user.isOrgAdmin && (
              <span className="pill pill-success">{dv.orgAdminBadge}</span>
            )}
            {user.orgName && (
              <span className="mono-meta text-muted-foreground">{user.orgName}</span>
            )}
          </div>
        </div>
      </header>

      {/* Name is managed in Wapplify Office (single source of truth). */}
      <section className="card-paper p-5 space-y-4">
        <h2 className="font-serif text-xl text-ink leading-tight">{dv.users.name}</h2>
        <p className="text-sm text-foreground">{user.name}</p>
        <p className="text-sm text-muted-foreground">{dv.users.nameInOffice}</p>
      </section>

      {/* Password is managed in Wapplify Office (single source of truth). */}
      <section className="card-paper p-5 space-y-4">
        <h2 className="font-serif text-xl text-ink leading-tight">{dv.auth.changePassword}</h2>
        <p className="text-sm text-muted-foreground">{dv.users.passwordInOffice}</p>
        <a
          href={OFFICE_PROFILE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="btn-primary"
        >
          {dv.users.openOffice}
          <Icon name="open_in_new" size={18} />
        </a>
      </section>

      {hasZone(user) && <TelegramCard />}
    </div>
  );
}
